import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { emailService, MessagePerformanceRow } from '../services/emailService';

const MessagePerformance: React.FC = () => {
  const [rows, setRows] = useState<MessagePerformanceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await emailService.getMessagePerformance();
      setRows(res.results ?? []);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="max-w-6xl mx-auto space-y-6 px-4 py-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-border pb-4">
        <div>
          <h1 className="text-2xl font-bold text-fg">Message performance</h1>
        </div>
        <Link to="/settings/email" className="btn-secondary text-sm py-2 px-3 rounded-lg shrink-0">
          Email settings
        </Link>
      </div>

      {error ? (
        <div className="alert-danger rounded-xl px-4 py-3 text-sm">{error}</div>
      ) : null}

      {loading ? (
        <p className="text-sm text-fg-muted">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-fg-muted rounded-xl border border-dashed border-border p-8 text-center">
          No sent messages yet. When you email leads from LaunchPoint, performance appears here.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-surface-muted/50 text-left text-fg-secondary border-b border-border">
                <th className="px-3 py-2 font-medium">Template</th>
                <th className="px-3 py-2 font-medium text-right">Sent</th>
                <th className="px-3 py-2 font-medium text-right">Replies</th>
                <th className="px-3 py-2 font-medium text-right">Positive</th>
                <th className="px-3 py-2 font-medium text-right">Neutral</th>
                <th className="px-3 py-2 font-medium text-right">Negative</th>
                <th className="px-3 py-2 font-medium text-right">Opt-out</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={`${r.template_id ?? 'x'}-${idx}`} className="border-t border-border">
                  <td className="px-3 py-2 text-fg font-medium">{r.template_name}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.sent_count}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.reply_count}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.positive_count}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.neutral_count}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.negative_count}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.opt_out_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MessagePerformance;
